import { useEffect, useState } from "react";
import { NOTE_CLASS } from "../../lib/ui.js";
import {
    countdownTone,
    describeTargetShape,
    formatCountdown,
    shortId,
    stateCopy,
} from "../../lib/escalationLabels.js";
import { subjectKey, subjectLabel } from "../../lib/escalationClient.js";
import { providerLabel } from "../../lib/identityClient.js";

function tickInterval(msRemaining) {
    return msRemaining <= 120000 ? 1000 : 20000;
}

export function Countdown({ expiresAt, className = "" }) {
    const [remaining, setRemaining] = useState(() => (expiresAt ? expiresAt.getTime() - Date.now() : 0));
    useEffect(() => {
        if (!expiresAt) return undefined;
        let timer = null;
        const tick = () => {
            const next = expiresAt.getTime() - Date.now();
            setRemaining(next);
            if (next > 0) timer = setTimeout(tick, tickInterval(next));
        };
        tick();
        return () => { if (timer) clearTimeout(timer); };
    }, [expiresAt]);
    if (!expiresAt) return null;
    const tone = countdownTone(remaining);
    return (
        <span
            className={[
                "whitespace-nowrap text-[11px] leading-[1.08] font-medium tabular-nums",
                tone === "ending" ? "text-red-400" : tone === "ended" ? "text-[color:var(--muted-soft)]" : "text-[color:var(--green)]",
                className,
            ].join(" ")}
        >
            {formatCountdown(remaining)}
        </span>
    );
}

// Which directory a person or group came from. A name is unique only inside
// its integration, so the provider travels with it everywhere it is shown.
export function ProviderTag({ provider }) {
    const label = providerLabel(provider);
    if (!label) return null;
    return (
        <span className="shrink-0 border border-[color:var(--line)] px-[5px] py-[1px] text-[9px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]">
            {label}
        </span>
    );
}

export function StateChip({ state }) {
    const copy = stateCopy(state);
    return (
        <span className="inline-flex items-center gap-[6px] whitespace-nowrap text-[11px] leading-[1.08] font-medium text-[color:var(--ink)]">
            <span className={`h-[7px] w-[7px] flex-none rounded-[999px] ${copy.dot}`} />
            {copy.label}
        </span>
    );
}

function PersonIcon() {
    return (
        <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" className="h-[11px] w-[11px] flex-none">
            <circle cx="8" cy="5.4" r="2.6" />
            <path d="M3.2 13.2a4.8 4.8 0 0 1 9.6 0" />
        </svg>
    );
}

function MachineIcon() {
    return (
        <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" className="h-[11px] w-[11px] flex-none">
            <rect x="2.2" y="3.2" width="11.6" height="7.6" rx="1" />
            <path d="M5.4 13.2h5.2" />
        </svg>
    );
}

export function TargetChip({ target, endpointNames = null, subjectNames = null, showShape = true }) {
    const shape = describeTargetShape(target);
    const machine = target?.endpointId ? endpointNames?.get(target.endpointId) : null;
    const person = target?.subject
        ? (subjectNames?.get(subjectKey(target.subject)) || subjectLabel(target.subject))
        : "";
    return (
        <span className="grid gap-[2px] min-w-0">
            <span className="flex flex-wrap items-center gap-x-[10px] gap-y-[3px] min-w-0">
                {target?.subject ? (
                    <span className="inline-flex items-center gap-[5px] min-w-0 text-[12px] leading-[1.14] text-[color:var(--ink)]">
                        <PersonIcon />
                        <span className="truncate font-medium" title={target.subject.sourceId}>
                            {person}
                        </span>
                        <ProviderTag provider={target.subject.sourceProvider} />
                    </span>
                ) : null}
                {target?.endpointId ? (
                    <span className="inline-flex items-center gap-[5px] min-w-0 text-[12px] leading-[1.14] text-[color:var(--ink)]">
                        <MachineIcon />
                        {machine?.hostname ? (
                            <span className="truncate font-medium" title={machine.hostname}>
                                {machine.hostname}
                                {machine.registeredUser ? (
                                    <span className="ml-[6px] font-normal text-[color:var(--muted)]">{machine.registeredUser}</span>
                                ) : null}
                            </span>
                        ) : (
                            <span className="truncate font-mono text-[11px]" title={target.endpointId}>
                                {shortId(target.endpointId)}
                            </span>
                        )}
                    </span>
                ) : null}
            </span>
            {showShape ? (
                <span className={`${NOTE_CLASS} text-[10px]`}>{shape.sentence}.</span>
            ) : null}
        </span>
    );
}

export function Action({ onClick, children, tone = "default", disabled = false }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={[
                "h-[28px] border px-[11px] text-[11px] uppercase tracking-[0.08em] transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer",
                tone === "primary"
                    ? "border-[color:var(--green)] bg-[color:var(--green-soft)] font-medium text-[color:var(--ink)]"
                    : tone === "danger"
                        ? "border-red-400/50 bg-transparent text-red-400 hover:bg-red-400/10"
                        : "border-[color:var(--line)] bg-transparent text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)]",
            ].join(" ")}
        >
            {children}
        </button>
    );
}
